import { useRef } from 'react';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { SubmissionCardModel, SubmissionRow } from './types';

interface SubmissionControlCardProps {
  data: SubmissionCardModel;
  measureOptions: any[];
  submissionOptions: SubmissionRow[];
  selectedMeasures?: any[];
  onMeasureChange: (value: any[]) => void;
  onCalculateClick?: () => void;
  onInitialToggle: () => void;
  onResolutionToggle: () => void;
  onSubmissionChange: (submissionIds: string[]) => void;
  onPublishClick?: () => void;
  onDistributeClick?: () => void;
  publishLoading?: boolean;
  distributeLoading?: boolean;
  measureSyncLoading?: boolean;
  onMeasureSyncClick?: () => void;
  submissionsLoading?: boolean;
  initialToggleLoading?: boolean;
  resolutionToggleLoading?: boolean;
  initialToggleDisabled?: boolean;
}

const SubmissionControlCard = ({
  data,
  measureOptions,
  submissionOptions,
  selectedMeasures = [],
  onMeasureChange,
  onCalculateClick,
  onInitialToggle,
  onResolutionToggle,
  onSubmissionChange,
  onPublishClick,
  onDistributeClick,
  publishLoading = false,
  distributeLoading = false,
  measureSyncLoading = false,
  onMeasureSyncClick,
  submissionsLoading = false,
  initialToggleLoading = false,
  resolutionToggleLoading = false,
  initialToggleDisabled = false
}: SubmissionControlCardProps) => {
  const multiSelectRef = useRef<any>(null);

  return (
    <article className='submission-card'>
      <h3>{data.title}</h3>

      <div className='submission-field-row'>
        <label>Select Measure for Calculation</label>
        <MultiSelect
          ref={multiSelectRef}
          placeholder="Select Measures"
          value={selectedMeasures}
          options={measureOptions}
          onChange={(e) => onMeasureChange(e.value)}
          optionLabel="measureNumber"
          optionValue="measureNumber"
          display="chip"
          maxSelectedLabels={3}
          className='w-full'
        />
      </div>

      <div className='submission-action-row'>
        <button
          className='admin-btn admin-btn-secondary'
          type='button'
          disabled={data.calculateDisabled}
          onClick={onCalculateClick}
        >
          Calculate and Publish to Scoring Portal
        </button>

        <Button
          label='Publish to Data Submission Portal'
          className='admin-btn admin-btn-primary'
          type='button'
          disabled={data.publishDisabled || publishLoading}
          loading={publishLoading}
          loadingIcon='pi pi-spin pi-spinner'
          onClick={onPublishClick}
        />
      </div>

      <hr />

      <div className='submission-switch-row'>
        <span>Initial Review Cycle</span>
        {initialToggleLoading ? (
          <i
            className='pi pi-spin pi-spinner'
            style={{
              fontSize: '1.2rem',
              color: '#6c757d',
              margin: '0 0.5rem'
            }}
          />
        ) : (
          <button
            type='button'
            className={`admin-switch ${data.initialReviewCycleEnabled ? 'on' : 'off'}${initialToggleDisabled ? ' disabled' : ''}`}
            onClick={onInitialToggle}
            disabled={initialToggleDisabled}
            aria-label='Toggle initial review cycle'
            title={initialToggleDisabled ? 'End Resolution Review Cycle first to toggle Initial Review Cycle' : ''}
          >
            <span />
          </button>
        )}
        <strong>{data.initialReviewCycleEnabled ? 'ON' : 'OFF'}</strong>
      </div>

      <div className='submission-switch-row'>
        <span>Resolution Review Cycle</span>
        {resolutionToggleLoading ? (
          <i
            className='pi pi-spin pi-spinner'
            style={{
              fontSize: '1.2rem',
              color: '#6c757d',
              margin: '0 0.5rem'
            }}
          />
        ) : (
          <button
            type='button'
            className={`admin-switch ${data.resolutionReviewCycleEnabled ? 'on' : 'off'}`}
            onClick={onResolutionToggle}
            aria-label='Toggle resolution review cycle'
          >
            <span />
          </button>
        )}
        <strong>{data.resolutionReviewCycleEnabled ? 'ON' : 'OFF'}</strong>
      </div>

      <hr />

      <div className='submission-field-row multi'>
        <label>Select Submissions for Letter Distribution</label>
        {submissionsLoading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              minHeight: '80px',
              justifyContent: 'center'
            }}
          >
            <i
              className='pi pi-spin pi-spinner'
              style={{
                fontSize: '1.2rem',
                color: '#6c757d'
              }}
            />
            <span
              style={{
                color: '#6c757d',
                fontSize: '0.9rem'
              }}
            >
              Loading providers...
            </span>
          </div>
        ) : (
          <select
            multiple
            value={data.selectedSubmissions}
            onChange={(event) => {
              const selected = Array.from(event.target.selectedOptions).map(
                (option) => option.value
              );
              onSubmissionChange(selected);
            }}
          >
            {submissionOptions.map((submission) => (
              <option key={submission.id} value={submission.id}>
                {submission.label}
              </option>
            ))}
          </select>
        )}
      </div>

      <Button
        label={data.distributeLabel}
        className='admin-btn admin-btn-solid-full'
        type='button'
        loading={distributeLoading}
        loadingIcon='pi pi-spin pi-spinner'
        disabled={distributeLoading}
        onClick={onDistributeClick}
      />

      <Button
        label={data.measureSyncLabel}
        className='admin-btn admin-btn-solid-full'
        type='button'
        loading={measureSyncLoading}
        loadingIcon='pi pi-spin pi-spinner'
        disabled={measureSyncLoading}
        onClick={onMeasureSyncClick}
      />
    </article>
  );
};

export default SubmissionControlCard;